import { useCallback, useEffect, useRef, useState } from 'react';
import { useMount } from 'react-use';
import videojs, { VideoJsPlayer, VideoJsPlayerOptions } from 'video.js';
import 'video.js/dist/video-js.css';

const SKIP_SECOND = 0.05;

export interface VideoActions {
    onChangePlayBackRate: (rate: number) => void;
    skipPlus: () => void;
    skipMinus: () => void;
    skip: (second: number) => void;
    skipByRatio: (ratio: number) => void;
    togglePlay: () => Promise<void>;
    fullScreen: () => void;
}

interface VideoJS {
    // build通すための一時的処置。 https://unipro.backlog.jp/view/26_GOLF-159 で対応。
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Video: ({ children, ...props }: any) => JSX.Element;
    player: VideoJsPlayer | undefined;
    thumbnails: [string, number][] | undefined;
    actions: VideoActions;
}

export const baseOptions: VideoJsPlayerOptions = {
    height: 700,
    width: 550,
    controls: true,
    controlBar: {
        volumePanel: true,
        playToggle: false,
        remainingTimeDisplay: false,
        progressControl: true,
        playbackRateMenuButton: true,
        pictureInPictureToggle: false,
        currentTimeDisplay: true,
    },
    playbackRates: [0.0625, 0.1, 0.5, 1, 1.5],
    responsive: true,
};

const useVideoJS = (videoJsOptions: VideoJsPlayerOptions): VideoJS => {
    const videoNode = useRef<HTMLVideoElement>(null);
    const [player, setPlayer] = useState<VideoJsPlayer>();
    const [thumbnails, setThumbnails] = useState<[string, number][]>();

    useMount(() => {
        if (videoNode.current) {
            setPlayer(
                videojs(videoNode.current, videoJsOptions, function (this) {
                    // do anything
                }),
            );
            if (player) {
                return () => {
                    player.dispose();
                };
            }
        }
    });

    useEffect(() => {
        const THUMBNAIL_COUNT = 10;
        const thumbs: [string, number][] = [];
        if (!videoNode.current) return;
        const videoNodeRef = videoNode.current;
        const canvas = document.createElement('canvas');
        const video = document.createElement('video');
        let interval = 0;
        let isLoadedVideoNode = false;
        let isLoadedVideo = false;
        video.crossOrigin = 'anonymous';

        // サムネイル取得処理
        const captureThumbnail = () => {
            const ctx = canvas.getContext('2d');
            if (thumbs.length < THUMBNAIL_COUNT && ctx) {
                // 初回だけ数秒先のフレームを表示する
                const displayTime = thumbs.length === 0 ? 0 : video.currentTime;
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                thumbs.push([canvas.toDataURL('image/png'), displayTime]);
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                video.currentTime += interval;
            } else {
                // 取得完了したらサムネイル用の動画を停止
                video.pause();
                // safari考慮のためイベント付与
                video.dispatchEvent(new Event('pause'));
            }
        };

        // サムネイル取得用の動画が再生可能になった時のイベント
        const onVideoCanPlayThrough = () => {
            if (isLoadedVideo) {
                //イベントセット後は処理しない
                return;
            }
            isLoadedVideo = true;
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            interval = Number(
                (video.duration / (THUMBNAIL_COUNT - 1)).toFixed(2),
            );
            // 0秒時のサムネイルの取得
            captureThumbnail();
            // currentTime更新後の取得
            video.addEventListener('seeked', captureThumbnail);
        };

        // サムネイル取得元の動画が再生可能になった時のイベント
        const onVideoNodeCanPlayThrough = () => {
            // 親ビデオのシーク等でイベントが発火しないよう初回のみ処理する
            if (videoNode.current && !isLoadedVideoNode) {
                isLoadedVideoNode = true;
                video.src = videoNode.current.src;
                video.currentTime = 0.01;
                video.addEventListener('canplaythrough', onVideoCanPlayThrough);
            }
        };

        // 付与したイベントの削除
        const removeLoadEvent = () => {
            videoNodeRef.removeEventListener(
                'canplaythrough',
                onVideoNodeCanPlayThrough,
            );
            video.removeEventListener('canplaythrough', onVideoCanPlayThrough);
            video.removeEventListener('seeked', captureThumbnail);
        };

        // サムネイル取得用の動画が停止になった時のイベント
        const endedEvent = () => {
            setThumbnails(thumbs);
            removeLoadEvent();
        };

        // サムネイル取得元の動画が再生可能になった時
        videoNodeRef.addEventListener(
            'canplaythrough',
            onVideoNodeCanPlayThrough,
        );

        // サムネイル取得用の動画が停止になった時
        video.addEventListener('ended', endedEvent);

        return () => {
            removeLoadEvent();
            video.removeEventListener('ended', endedEvent);
            video.remove();
            videoNodeRef.remove();
        };
    }, [videoNode]);

    const onChangePlayBackRate = useCallback(
        (rate: number) => {
            player?.playbackRate(rate);
        },
        [player],
    );
    const skipPlus = useCallback(() => {
        player?.currentTime(player.currentTime() + SKIP_SECOND);
    }, [player]);
    const skipMinus = useCallback(() => {
        player?.currentTime(player.currentTime() - SKIP_SECOND);
    }, [player]);
    const fullScreen = useCallback(() => {
        player?.requestFullscreen();
    }, [player]);
    const skip = useCallback(
        (second: number) => {
            player?.currentTime(second);
        },
        [player],
    );
    const skipByRatio = useCallback(
        (ratio: number) => {
            // 指定可能なのは0～100までとする
            if (!player || ratio > 100 || ratio < 0) {
                return;
            }

            player.currentTime(player.duration() * (ratio / 100));
        },
        [player],
    );
    const togglePlay = useCallback(async () => {
        if (player?.paused() === true) {
            await player.play();
            return;
        }
        if (player?.paused() === false) {
            player.pause();
            return Promise.resolve();
        }
    }, [player]);
    const actions: VideoActions = {
        onChangePlayBackRate,
        skipPlus,
        skipMinus,
        skip,
        skipByRatio,
        togglePlay,
        fullScreen,
    };

    const Video = useCallback(({ children, ...props }) => {
        return (
            <div data-vjs-player=''>
                <video
                    ref={videoNode}
                    className='video-js vjs-big-play-centered'
                    {...props}
                >
                    {children}
                </video>
            </div>
        );
    }, []);
    return { Video, player, thumbnails, actions };
};

export default useVideoJS;
